import React from "react";

import { Box, Grid, Typography } from "@mui/material";
import { makeStyles } from "tss-react/mui";

import { NCIcon } from "@/components/Icons/Icon";
import { graphql } from "@/gql";
import { useFragment } from "@/hooks/useFragment";
import { colors } from "@/theme/overrides/colors";
import { format } from "@/utils/datetime";
import { ordinal } from "@/utils/number";

interface Props {
  pregnancyHistoryId: string;
}

const useStyles = makeStyles()({
  textValue: {
    textTransform: "uppercase",
  },
});

const getUnit = (length: number, noun: string) => {
  if (length === 1) {
    return noun;
  }
  return noun + "s";
};

const pregnancyHistoryFragment = graphql(`
  fragment ClientPregnancyHistory on PregnancyHistory {
    id
    clientId
    currentlyPregnant
    numberOfPregnancies
    gestationalType
    lastMenstruationOn
    gestationalAgeWeeks
    pregnancyObservation
    currentlyLactating
    lactationStartsOn
    lactationDurationMonths
    createdAt
    updatedAt
  }
`);

const PregnancyHistory: React.FC<Props> = ({ pregnancyHistoryId }) => {
  const { classes } = useStyles();
  const [pregnancy] = useFragment(
    pregnancyHistoryFragment,
    `PregnancyHistory:${pregnancyHistoryId}`
  );
  const gestationalType = !pregnancy?.gestationalType
    ? "N/A"
    : `${pregnancy?.gestationalType}`;
  const gestationalAge = !pregnancy?.gestationalAgeWeeks
    ? "N/A"
    : `${pregnancy?.gestationalAgeWeeks} ${getUnit(
        pregnancy?.gestationalAgeWeeks,
        "Week"
      )}`;
  const pregnancyCount = !pregnancy?.numberOfPregnancies
    ? "N/A"
    : `${ordinal(pregnancy?.numberOfPregnancies)} pregnancy`;
  const firstDayOfLactation = !pregnancy?.lactationStartsOn
    ? "N/A"
    : `${format({ date: pregnancy?.lactationStartsOn }, "do MMM, yyy")}`;
  const lactationDuration = !pregnancy?.lactationDurationMonths
    ? "N/A"
    : `${pregnancy?.lactationDurationMonths} ${getUnit(
        pregnancy?.lactationDurationMonths,
        "Month"
      )}`;

  return (
    <>
      <div>
        <Box display="flex" alignItems="center" marginBottom="12px">
          <NCIcon icon="person-pregnant" htmlColor={colors.grey} mr="normal" />
          <Typography variant="body1">Pregnancy History</Typography>
        </Box>
        <Typography
          variant="body2"
          color="textSecondary"
          className={classes.textValue}
        >
          {pregnancyCount}
        </Typography>
      </div>

      {pregnancy?.currentlyPregnant && (
        <Box display="flex" alignItems="start" marginTop={5}>
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <Typography variant="body1" color="textSecondary">
                Gestational Type
              </Typography>
              <Typography
                variant="body2"
                color="textSecondary"
                className={classes.textValue}
              >
                {gestationalType}
              </Typography>
            </Grid>

            <Grid item xs={6}>
              <Typography variant="body1" color="textSecondary">
                Gestational Age
              </Typography>
              <Typography variant="body1">{gestationalAge}</Typography>
            </Grid>
          </Grid>
        </Box>
      )}

      {pregnancy?.currentlyLactating && (
        <Box display="flex" alignItems="start" marginTop={5}>
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <Typography variant="body1" color="textSecondary">
                Beginning of lactation
              </Typography>
              <Typography variant="body1">{firstDayOfLactation}</Typography>
            </Grid>

            <Grid item xs={6}>
              <Typography variant="body1" color="textSecondary">
                Duration of lactation
              </Typography>
              <Typography variant="body1">{lactationDuration}</Typography>
            </Grid>
          </Grid>
        </Box>
      )}

      {pregnancy?.pregnancyObservation && (
        <Box display="flex" alignItems="start" marginTop={5}>
          <Typography variant="body2" color="textSecondary">
            {pregnancy?.pregnancyObservation}
          </Typography>
        </Box>
      )}
    </>
  );
};

export default PregnancyHistory;
